import React, { FC, RefObject } from 'react';
import { usePostComposer } from 'app/modules/posts/modules/post/components/composer/PostComposerProvider';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import PostComposerContentFacebook from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentFacebook';
import PostComposerContentInstagram from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentInstagram';
import PostComposerContentGoogleMyProfile from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentGoogleMyProfile';
import { PostComposerFormState } from 'app/modules/posts/modules/post/components/composer/utils/postComposerFormState';
import { VideoPlayerRef } from '@kontentino/kontentino-post-previews';
import PostComposerContentPinterest from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentPinterest';
import PostComposerContentTwitter from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentTwitter';
import PostComposerContentLinkedin from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentLinkedin';
import AccordionWidget from 'app/modules/posts/components/AccordionWidget';
import AccordionWidgetIcon from 'app/modules/posts/components/accordionWidget/AccordionWidgetIcon';
import AccordionWidgetTitle from 'app/modules/posts/components/accordionWidget/AccordionWidgetTitle';
import PostComposerContentTiktok from './postComposerContent/PostComposerContentTiktok';
import PostComposerContentYoutube from './postComposerContent/PostComposerContentYoutube';
import { PostComposerFormOptions } from 'app/modules/posts/modules/post/components/composer/hooks/usePostComposerFormOptions';
import { UseSetStateAction } from 'utils/hooks/useSetState';
import PostComposerContentThreads from './postComposerContent/PostComposerContentThreads';

type Props = {
  accordionProps?: Omit<
    React.ComponentProps<typeof AccordionWidget>,
    'title' | 'titleSuffix' | 'icon'
  >;
};

export type PostComposerContentProps = {
  value: PostComposerFormState;
  onChange: UseSetStateAction<PostComposerFormState>;
  options: PostComposerFormOptions;
  onOptionsChange: UseSetStateAction<PostComposerFormOptions>;
  videoPlayerRef: RefObject<VideoPlayerRef>;
  onTextEditorInit: (editor: any) => void;
  onTranslationEditorInit: (editor: any) => void;
  swapTextTranslation: () => void;
};

export const PostComposerContent: FC<Props> = ({ accordionProps }) => {
  const {
    form,
    setForm,
    options,
    setOptions,
    videoPlayerRef,
    onTextEditorInit,
    onTranslationEditorInit,
    swapTextTranslation,
  } = usePostComposer();

  function getChildContent() {
    const props: PostComposerContentProps = {
      value: form,
      onChange: setForm,
      options,
      onOptionsChange: setOptions,
      onTextEditorInit,
      onTranslationEditorInit,
      swapTextTranslation,
      videoPlayerRef,
    };

    const pageType = form.page.type;

    switch (pageType) {
      case TYPE.INSTAGRAM:
        return <PostComposerContentInstagram {...props} />;
      case TYPE.FACEBOOK:
        return <PostComposerContentFacebook {...props} />;
      case TYPE.GOOGLE_MY_BUSINESS:
        return <PostComposerContentGoogleMyProfile {...props} />;
      case TYPE.LINKEDIN:
        return <PostComposerContentLinkedin {...props} />;
      case TYPE.PINTEREST:
        return <PostComposerContentPinterest {...props} />;
      case TYPE.TWITTER:
        return <PostComposerContentTwitter {...props} />;
      case TYPE.YOUTUBE:
        return <PostComposerContentYoutube {...props} />;
      case TYPE.TIKTOK:
        return <PostComposerContentTiktok {...props} />;
      case TYPE.THREADS:
        return <PostComposerContentThreads {...props} />;
    }

    return null;
  }

  return (
    <AccordionWidget
      title={<AccordionWidgetTitle variant="postContent" />}
      icon={<AccordionWidgetIcon variant="postContent" />}
      data-name="post-composer_post-content"
      {...accordionProps}
    >
      <div className="tw-flex tw-flex-col tw-gap-4">{getChildContent()}</div>
    </AccordionWidget>
  );
};
